/**
 * Veo 3.1 视频生成（通过后端服务器调用 Google Gemini Veo 3.1 API）
 *
 * 后端接口（参考 Veo 3.0 中转模式）：
 *   POST /web/v1/veo/v3-1/createVideo   — 提交生成任务
 *   POST /web/v1/veo/v3-1/getTaskResult — 查询任务结果
 *
 * Veo 3.1 REST API 参数（由后端转发到 Gemini）：
 *   - prompt: 文本提示（最长 2000 字符）
 *   - negativePrompt: 负面提示词（可选）
 *   - image: 参考图片 URL（图生视频时必填）
 *   - imageTail: 尾帧图片 URL（可选，插值模式）
 *   - aspectRatio: "16:9"（默认）| "9:16"
 *   - resolution: "720p"（默认）| "1080p"（仅 8s）| "4k"
 *   - videoDuration: 4 | 6 | 8（秒）
 *   - generateAudio: 是否生成音频（默认 true）
 *   - seed: 种子值（可选）
 *   - personGeneration: "allow_all" | "allow_adult"（可选）
 *   - veoGenType: "fast" | "quality"（决定后端使用 veo-3.1-generate-preview 还是 veo-3.1-fast-generate-preview）
 *
 * 认证方式：Bearer token（由 apiClient 自动注入）
 */
import apiClient from '../../../config'

/** 轮询间隔和超时 */
var POLL_INTERVAL = 10000
var POLL_TIMEOUT = 10 * 60 * 1000

/** 后端接口路径 */
var CREATE_PATH = '/veo/v3-1/createVideo'
var RESULT_PATH = '/veo/v3-1/getTaskResult'

/**
 * 获取用户登录凭证
 */
function getMiToken() {
  return localStorage.getItem('token') || ''
}

/**
 * Veo 3.1 API 约束规则（官方直连专用，Pollo/AllAll 不受此限）：
 *
 * 分辨率 & 时长：
 *   - 720p：支持 4s / 6s / 8s
 *   - 1080p：仅支持 8s
 *
 * 宽高比：
 *   - 720p 和 1080p 均支持 16:9 和 9:16
 *
 * 参考图片 + 尾帧（图生视频）：
 *   - 有尾帧（插值模式）：时长必须为 8s，支持 16:9 和 9:16
 */

/**
 * Veo 3.1 API 约束规则（官方直连专用，Pollo/AllAll 不受此限）：
 *
 * 分辨率 & 时长：
 *   - 720p：支持 4s / 6s / 8s
 *   - 1080p：仅支持 8s
 *   - 4k：仅支持 8s
 *
 * 宽高比：
 *   - 720p / 1080p / 4k 均支持 16:9 和 9:16
 *
 * 参考图片 / 尾帧 / 扩展：时长必须为 8s
 */

/** 自动修正不合规参数（兜底保护，正常情况前端 UI 已阻止无效组合） */
function fixParams(p) {
  var res = String(p.videoRes || '720p').toLowerCase()
  var dur = parseInt(p.videoDuration, 10) || 8

  // 1080p / 4k 仅支持 8s
  if ((res === '1080p' || res === '4k') && dur !== 8) {
    p.videoDuration = 8
  }

  // 插值模式 / 参考图片时长必须 8s
  if ((p.imageTail || p.referenceImage) && dur !== 8) {
    p.videoDuration = 8
  }
}

/**
 * 构建提交参数（与前端页面参数一致，映射到 Veo 3.1 API 规范）
 */
function buildSubmitParams(params) {
  var token = getMiToken()
  var result = {
    miToken: token,
    prompt: (params.prompt || '').trim().slice(0, 2000),
    videoDuration: params.videoDuration || 8,
    videoRes: params.videoRes || '720p',
    aspectRatio: params.aspectRatio || '16:9',
    generateAudio: params.generateAudio !== false,
    veoGenType: params.veoGenType || 'fast'
  }

  // 负面提示词
  var negativePrompt = (params.negativePrompt || '').trim().slice(0, 2000)
  if (negativePrompt) {
    result.negativePrompt = negativePrompt
  }

  // 种子值
  var seed = params.seed != null && params.seed !== '' && params.seed !== -1 ? parseInt(params.seed, 10) : null
  if (seed != null && !isNaN(seed)) {
    result.seed = seed
  }

  // 图生视频：参考图片 URL
  var hasImage = params.referenceImage && typeof params.referenceImage === 'string' && params.referenceImage.trim().length > 0
  if (hasImage) {
    result.referenceImage = params.referenceImage.trim()
  }

  // 尾帧图片（插值模式）
  var hasEndFrame = params.imageTail && typeof params.imageTail === 'string' && params.imageTail.trim().length > 0
  if (hasEndFrame) {
    result.imageTail = params.imageTail.trim()
  }

  return { params: result, hasImage: hasImage, hasEndFrame: hasEndFrame }
}

/**
 * 提交 Veo 3.1 视频生成任务
 * @param {Object} params - 生成参数
 * @returns {Promise<{taskId: string, videoMode: string}>}
 */
export function submitGoogleVeo31Task(params) {
  var prompt = (params.prompt || '').trim()
  if (!prompt) return Promise.reject(new Error('prompt 必填'))

  var result = buildSubmitParams(params)

  // 官方直连参数修正（兜底）
  fixParams(result.params)

  return apiClient.post(CREATE_PATH, result.params).then(function (data) {
    var resData = data && data.data
    var taskId = resData && resData.taskId ? String(resData.taskId) : ''
    if (!taskId) return Promise.reject(new Error('后端接口未返回 taskId'))
    return {
      taskId: taskId,
      videoMode: result.hasImage ? 'image' : (params.videoMode || 'text')
    }
  })
}

/**
 * 解析后端返回的任务状态
 */
function parseTaskStatus(data) {
  var status = data && data.status ? String(data.status).toLowerCase() : ''
  if (status === 'succeed' || status === 'completed') {
    return { status: 'completed', url: data.videoUrl || null, error: null }
  }
  if (status === 'failed') {
    return { status: 'failed', url: null, error: data.message || '生成失败' }
  }
  // processing / pending / queued 等
  return { status: 'processing', url: null, error: null }
}

/**
 * 轮询 Veo 3.1 任务状态
 * @param {string} taskId - 创建任务返回的 taskId
 * @param {function} callback - 回调函数 (err, { status, url, error })
 * @param {number} interval - 轮询间隔（毫秒）
 */
export function pollGoogleVeo31Task(taskId, callback, interval) {
  var pollInterval = interval || POLL_INTERVAL
  var deadline = Date.now() + POLL_TIMEOUT
  var pollTimer = null
  var stopped = false
  var resolvingSuccess = false

  function stop() {
    stopped = true
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  function poll() {
    if (stopped) return
    apiClient
      .post(RESULT_PATH, { miToken: getMiToken(), taskId: taskId })
      .then(function (data) {
        if (stopped) return
        var resData = data && data.data
        if (!resData) {
          if (typeof callback === 'function') callback(new Error('查询结果为空'), null)
          stop()
          return
        }
        var parsed = parseTaskStatus(resData)

        if (parsed.status === 'completed') {
          if (resolvingSuccess) return
          resolvingSuccess = true
          if (typeof callback === 'function') {
            callback(null, { status: 'completed', url: parsed.url, error: null })
          }
          stop()
          return
        }

        if (parsed.status === 'failed') {
          if (typeof callback === 'function') callback(null, { status: 'failed', url: null, error: parsed.error })
          stop()
          return
        }

        // 处理中
        if (typeof callback === 'function') callback(null, { status: 'processing', url: null, error: null })
        if (Date.now() > deadline) {
          if (typeof callback === 'function') callback(new Error('轮询超时'), null)
          stop()
        } else {
          pollTimer = setTimeout(poll, pollInterval)
        }
      })
      .catch(function (err) {
        if (stopped) return
        if (typeof callback === 'function') callback(err, null)
        stop()
      })
  }

  poll()
  return { stop: stop }
}
